<template>
  <div class="profile-page" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
    <v-container class="profile-container">
      <!-- Profile Header -->
      <v-card class="profile-header-card mb-6" elevation="0">
        <div class="profile-header-content">
          <div class="profile-avatar-section">
            <div class="avatar-wrapper">
              <v-img
                v-if="userData.image"
                :src="userData.image"
                :alt="userData.name"
                class="profile-avatar"
                cover
              ></v-img>
              <v-icon
                v-else
                icon="mdi-account-circle"
                size="80"
                class="profile-avatar-placeholder"
              ></v-icon>
            </div>
            <div class="profile-info">
              <h1 class="profile-name">{{ userData.name || $t('Profile') }}</h1>
              <p class="profile-email" v-if="userData.email">{{ userData.email }}</p>
              <p class="profile-phone" v-if="userData.phone">{{ userData.phone }}</p>
            </div>
          </div>
          <v-btn
            class="edit-profile-btn"
            variant="outlined"
            color="primary"
            @click="navigateTo('/account-settings')"
          >
            <v-icon icon="mdi-pencil" class="me-2"></v-icon>
            {{ $t('Account_settings') }}
          </v-btn>
        </div>
      </v-card>

      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <v-row>
          <v-col cols="12" sm="6" md="4" v-for="i in 6" :key="i">
            <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
          </v-col>
        </v-row>
      </div>

      <!-- Profile Menu Items -->
      <div v-else>
        <!-- Main Actions -->
        <div class="menu-section mb-6">
          <h2 class="section-title">
            <v-icon icon="mdi-menu" class="me-2"></v-icon>
            {{ $t('Main_Actions') || 'Main Actions' }}
          </h2>
          <v-row>
            <v-col
              cols="12"
              sm="6"
              md="4"
              v-for="item in mainItems"
              :key="item.route"
            >
              <v-card
                class="menu-card"
                elevation="2"
                @click="navigateTo(item.route)"
              >
                <div class="menu-card-content">
                  <div class="menu-icon-wrapper" :style="{ backgroundColor: item.color }">
                    <v-icon :icon="item.icon" size="28" color="white"></v-icon>
                  </div>
                  <div class="menu-text">
                    <h3 class="menu-title">{{ $t(item.text) }}</h3>
                  </div>
                  <v-icon icon="mdi-chevron-left" class="menu-arrow" v-if="$i18n.locale === 'ar'"></v-icon>
                  <v-icon icon="mdi-chevron-right" class="menu-arrow" v-else></v-icon>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Settings -->
        <div class="menu-section mb-6">
          <h2 class="section-title">
            <v-icon icon="mdi-cog" class="me-2"></v-icon>
            {{ $t('Settings') || 'Settings' }}
          </h2>
          <v-row>
            <v-col
              cols="12"
              sm="6"
              md="4"
              v-for="item in settingsItems"
              :key="item.route"
            >
              <v-card
                class="menu-card"
                elevation="2"
                @click="navigateTo(item.route)"
              >
                <div class="menu-card-content">
                  <div class="menu-icon-wrapper" :style="{ backgroundColor: item.color }">
                    <v-icon :icon="item.icon" size="28" color="white"></v-icon>
                  </div>
                  <div class="menu-text">
                    <h3 class="menu-title">{{ $t(item.text) }}</h3>
                  </div>
                  <v-icon icon="mdi-chevron-left" class="menu-arrow" v-if="$i18n.locale === 'ar'"></v-icon>
                  <v-icon icon="mdi-chevron-right" class="menu-arrow" v-else></v-icon>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Information -->
        <div class="menu-section mb-6">
          <h2 class="section-title">
            <v-icon icon="mdi-information" class="me-2"></v-icon>
            {{ $t('Information') || 'Information' }}
          </h2>
          <v-row>
            <v-col
              cols="12"
              sm="6"
              md="4"
              v-for="item in infoItems"
              :key="item.route"
            >
              <v-card
                class="menu-card"
                elevation="2"
                @click="navigateTo(item.route)"
              >
                <div class="menu-card-content">
                  <div class="menu-icon-wrapper" :style="{ backgroundColor: item.color }">
                    <v-icon :icon="item.icon" size="28" color="white"></v-icon>
                  </div>
                  <div class="menu-text">
                    <h3 class="menu-title">{{ $t(item.text) }}</h3>
                  </div>
                  <v-icon icon="mdi-chevron-left" class="menu-arrow" v-if="$i18n.locale === 'ar'"></v-icon>
                  <v-icon icon="mdi-chevron-right" class="menu-arrow" v-else></v-icon>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Logout Button -->
        <v-card class="logout-card" elevation="2">
          <v-btn
            class="logout-btn"
            color="error"
            variant="elevated"
            size="large"
            block
            @click="handleLogout"
            :loading="logoutLoading"
          >
            <v-icon icon="mdi-logout" class="me-2"></v-icon>
            {{ $t('logout') }}
          </v-btn>
        </v-card>
      </div>
    </v-container>
  </div>
</template>

<script>
import crudDataService from "@/Services/crudDataService";
import { useAuth } from "../axios";

export default {
  name: "ProfileView",
  data() {
    return {
      loading: true,
      logoutLoading: false,
      userData: {},
      mainItems: [
        { text: "My_ads", route: "/my-ads", icon: "mdi-view-grid", color: "#0074c7" },
        { text: "Favorite_ads", route: "/favorite-ads", icon: "mdi-heart", color: "#dc3545" },
        { text: "Subscriptions", route: "/subscriptions", icon: "mdi-crown", color: "#ffc107" },
        { text: "chat", route: "/chats", icon: "mdi-message-text", color: "#17a2b8" },
        { text: "Followers", route: "/followers", icon: "mdi-account-group", color: "#28a745" },
        { text: "Commission", route: "/commission", icon: "mdi-cash-multiple", color: "#6f42c1" },
      ],
      settingsItems: [
        { text: "Account_settings", route: "/account-settings", icon: "mdi-account-cog", color: "#0074c7" },
        { text: "Blocked_Persons", route: "/blocked-persons", icon: "mdi-account-cancel", color: "#6c757d" },
        { text: "Site_balance", route: "/site-balance", icon: "mdi-wallet", color: "#28a745" },
      ],
      infoItems: [
        { text: "About", route: "/about-us", icon: "mdi-information-outline", color: "#0074c7" },
        { text: "Terms", route: "/terms", icon: "mdi-file-document-outline", color: "#17a2b8" },
        { text: "Adsconditions", route: "/ads-conditions", icon: "mdi-clipboard-text-outline", color: "#ffc107" },
        { text: "privacy", route: "/privacy", icon: "mdi-shield-lock-outline", color: "#6f42c1" },
        { text: "Agreement", route: "/agreement", icon: "mdi-handshake", color: "#28a745" },
        { text: "idea_policy", route: "/idea-Policy", icon: "mdi-copyright", color: "#dc3545" },
      ],
    };
  },
  methods: {
    navigateTo(route) {
      this.$router.push(route);
    },
    async fetchUserProfile() {
      try {
        this.loading = true;
        const id = localStorage.getItem("userId");
        if (id) {
          const response = await crudDataService.get(`user-profile`, id);
          this.userData = response.data.data || {};
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      } finally {
        this.loading = false;
      }
    },
    async handleLogout() {
      try {
        this.logoutLoading = true;
        await crudDataService.getAll("logout");
        const { clearToken } = useAuth();
        clearToken();
        this.$router.push({ name: 'home' }).then(() => {
          window.location.reload();
        });
      } catch (error) {
        console.error("Logout error:", error);
        // Even if logout fails, clear local storage and redirect
        const { clearToken } = useAuth();
        clearToken();
        this.$router.push({ name: 'home' }).then(() => {
          window.location.reload();
        });
      } finally {
        this.logoutLoading = false;
      }
    },
  },
  mounted() {
    this.fetchUserProfile();
  },
};
</script>

<style lang="scss" scoped>
.profile-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 24px 0;
}

.profile-container {
  max-width: 1200px;
}

/* Profile Header */
.profile-header-card {
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.profile-header-content {
  padding: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
}

.profile-avatar-section {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
  min-width: 250px;
}

.avatar-wrapper {
  position: relative;
}

.profile-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 2px solid #e0e0e0;
}

.profile-avatar-placeholder {
  width: 80px;
  height: 80px;
  color: #9e9e9e;
}

.profile-info {
  color: #333;
}

.profile-name {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #333;
}

.profile-email,
.profile-phone {
  font-size: 14px;
  color: #666;
  margin: 2px 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.edit-profile-btn {
  font-weight: 500;
  padding: 10px 20px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
}

/* Menu Sections */
.menu-section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}

/* Menu Cards */
.menu-card {
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  height: 100%;
  border: 1px solid #e0e0e0;

  &:hover {
    border-color: #0074c7;
    box-shadow: 0 2px 8px rgba(0, 116, 199, 0.1) !important;
  }
}

.menu-card-content {
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
}

.menu-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.menu-text {
  flex: 1;
  min-width: 0;
}

.menu-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 0;
  line-height: 1.4;
}

.menu-arrow {
  color: #9e9e9e;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.menu-card:hover .menu-arrow {
  transform: translateX(4px);
}

[dir="rtl"] .menu-card:hover .menu-arrow {
  transform: translateX(-4px);
}

/* Logout Card */
.logout-card {
  border-radius: 8px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
}

.logout-btn {
  font-size: 16px;
  font-weight: 500;
  padding: 14px;
  border-radius: 6px;
  text-transform: none;

  &:hover {
    opacity: 0.9;
  }
}

/* Loading State */
.loading-container {
  padding: 24px 0;
}

/* Responsive Design */
@media (max-width: 960px) {
  .profile-header-content {
    flex-direction: column;
    text-align: center;
  }

  .profile-avatar-section {
    flex-direction: column;
    text-align: center;
  }

  .edit-profile-btn {
    width: 100%;
  }
}

@media (max-width: 600px) {
  .profile-page {
    padding: 16px 0;
  }

  .profile-header-content {
    padding: 20px;
  }

  .profile-name {
    font-size: 20px;
  }

  .profile-avatar,
  .profile-avatar-placeholder {
    width: 70px;
    height: 70px;
  }

  .section-title {
    font-size: 18px;
  }

  .menu-card-content {
    padding: 16px;
  }

  .menu-icon-wrapper {
    width: 44px;
    height: 44px;
  }

  .menu-title {
    font-size: 15px;
  }
}
</style>
